import { MOBILE_CUSTOM_SCHEME } from '@sit/core';
/**
 * Visit: `app/source/common/security.inc` in the Intacct codebase
 * or inspect element on the megamenu of the Intacct website to find the navop values.
 *
 * Note: each navop value corresponds to a specific page in Intacct. However, they should be tested to ensure they work as expected.
 * Tested navop values are marked with a comment.
 */
// Note: these are individual variables so that tree-shaking can remove unused values.
// `const NAVOPS = { EE: { ... }, PA: { ... } }` would be more DRY, but it would also be less tree-shakeable.
export const NAVOPS = {
    // PA values are only available for backwards compatibility and should not be used in new code.
    /** @deprecated */
    PA: {
        /** @deprecated */
        LISTS_TIMESHEET: 4806,
        /** @deprecated */
        LISTS_MY_TIMESHEET: 4818, // Works
        /** @deprecated */
        EDIT_MY_TIMESHEET: 4819, // Works, goes to staff timesheet
        // DELETE_MY_TIMESHEET: 4839,
        // ACTIVITIES_APPROVETIMESHEET: 4851,
        // LISTS_TIMESHEET_EDIT: 4807,
        // LISTS_TIMESHEET_CREATE: 4808,
        /** @deprecated */
        LISTS_TIMESHEET_DELETE: 4809,
        // LISTS_TIMESHEET_VIEW: 4810,
        /** @deprecated */
        CREATE_MY_TIMESHEET: 4838, // Works
        // VIEW_MY_TIMESHEET: 4852,
        // LISTS_TIMESHEET_APPROVE: 5996,
        // LISTS_TIMESHEET_DECLINE: 5997,
        // LISTS_TIMESHEET_REPOSITORY: 6301,
        /** @deprecated */
        LISTS_TIMESHEET_SITPREFERENCES: 6302,
        /** @deprecated */
        LISTS_TIMESHEET_SITTIMESOURCES: 6304,
        // ACTIVITIES_SITAPPROVETIMEENTRIES: 6308,
        // LISTS_TIMESHEET_MYTIMECLOCK: 6350, // Doesn't work
        // LISTS_TIMESHEET_TIMECLOCK: 6351, // Doesn't work
        // LISTS_RATECARDS: 6360, // Causes uncaught exception
    },
    TT: {
        // Takes us to Intacct's timesheet page, not SIT's
        // @deprecated
        // LISTS_TIMESHEET: 4860, //
        // LISTS_TIMESHEET_EDIT: 4878,
        // LISTS_TIMESHEET_CREATE: 4879, // create staff timesheet, works
        // LISTS_TIMESHEET_DELETE: 4886,
        // LISTS_TIMESHEET_VIEW: 4887,
        EDIT_MY_TIMESHEET: 4889,
        // DELETE_MY_TIMESHEET: 4891,
        // VIEW_MY_TIMESHEET: 4892,
        ACTIVITIES_APPROVETIMESHEET: 6303, // Works with `.isembedded=T`
        /** Throws error on Intacct */
        LISTS_TIMESHEET_SITTIMESOURCES: 6305,
        // LISTS_TIMESHEET_REPOSITORY: 6306,
        /** Throws error on Intacct */
        LISTS_TIMESHEET_SITPREFERENCES: 6307,
        // ACTIVITIES_SITAPPROVETIMEENTRIES: 6309, // Causes uncaught exception
        // LISTS_TIMESHEET_MYTIMECLOCK: 6340, // Doesn't work
        // LISTS_TIMESHEET_TIMECLOCK: 6341, // Doesn't work
        // LISTS_MY_TIMESHEET: 4888, // Visits Intacct's timesheet page, not SIT's
        APPROVE_TIME_ENTRIES: 6309,
        APPROVE_TIMESHEETS: 6303,
        CREATE_MY_TIMESHEET: 4890,
        REPOSITORY: 6301,
        /** Works with `.isembedded=T`. This is really confusing and may be an Intacct bug. This links is supposed to be for staff timesheets but it's taking us to `My timesheets`. */
        LISTS_MY_TIMESHEET: 4860, // This SHOULD be 4888 but that takes us to Intacct's timesheet page, not SIT's
        /** Works with `.isembedded=T`, create staff timesheets */
        CREATE_TIMESHEET: 4879,
        TIMESHEET_INQUIRY: 4954,
        TIME_SOURCES: 6305,
        LIST_MY_TIME_CLOCKS: 6340,
        LIST_TIME_CLOCKS: 6351, // staff
    },
};
/**
 * This function matches the behaviour of the `app/source/cpa/Autoslide.cls` class in the Intacct codebase.
 * The intent is to create a URL that will point to any page in Intacct.
 * It supports logging in as a specific user, and navigating to a specific page.
 *
 * @param companyId the Intacct company ID (e.g. `SIT-Tom`)
 * @param loginId the Intacct login ID (e.g. `tomholloway`)
 * @param params additional query parameters to be added to the URL 99% of the time this will be something like ".navop=3342."
 * @param baseUrl the base URL of the Intacct instance including `login.phtml` (e.g. `https://intacct.com/ia/acct/login.phtml`)
 * @returns
 * @example "https://www.intacct.com/ia/acct/login.phtml?.company=SIT-Tom&.login=tomholloway&.navop=3342"
 */
export function buildAutoslideLoginUrl(companyId, loginId, params, baseUrl = 'https://www.intacct.com/ia/acct/login.phtml') {
    const loginParam = loginId ? `&.login=${loginId}` : '';
    const paramStr = params?.toString();
    const paramQs = paramStr ? `&${paramStr}` : '';
    return `${baseUrl}?.company=${companyId}${loginParam}${paramQs}`;
}
/**
 * This function builds a URL for the Intacct login page with a specific navop value.
 * It is a convenience function that calls `buildAutoslideLoginUrl` with the navop value as a query parameter.
 * The navop value should be one of the constants defined in this module.
 *
 * @param companyId the Intacct company ID (e.g. `SIT-Tom`)
 * @param loginId the Intacct login ID (e.g. `tomholloway`)
 * @param navop the navop value to be added to the URL
 * @param baseUrl the base URL of the Intacct instance including `login.phtml` (e.g. `https://intacct.com/ia/acct/login.phtml`)
 * @returns the URL for the Intacct login page with the navop value
 * @example "https://www.intacct.com/ia/acct/login.phtml?.company=SIT-Tom&.login=tomholloway&.navop=4818"
 */
export function buildAutoslideLoginUrlWithNavop(companyId, loginId, navop, isEmbedded, baseUrl = 'https://www.intacct.com/ia/acct/login.phtml') {
    const params = new URLSearchParams();
    if (navop)
        params.append('.navop', navop.toString());
    if (isEmbedded)
        params.append('.isembedded', isEmbedded);
    return buildAutoslideLoginUrl(companyId, loginId, params, baseUrl);
}
/**
 * This function builds a URL for the Intacct login page with a specific record URL value.
 *
 * @param recordUrlValue the record URL value to be added to the URL
 * @param baseUrl the base URL of the Intacct instance including `ur.phtml` (e.g. `https://intacct.com/ia/acct/ur.phtml`)
 * @returns the URL for the Intacct login page with the record URL value
 * @example "https://www.intacct.com/ia/acct/ur.phtml?.r=s8mBYsNwpUxvbWbNJ9Oj-Vom1OBMzgynEbFNYHhdodf"
 */
export function buildAutoslideLoginUrlWithRecordUrl(recordUrlValue, isEmbedded, baseUrl = 'https://www.intacct.com/ia/acct/ur.phtml') {
    const params = new URLSearchParams({ '.r': recordUrlValue });
    if (isEmbedded)
        params.append('.isembedded', isEmbedded);
    return `${baseUrl}?${params.toString()}`;
}
/**
 * This function builds a deep link URL for the mobile app.
 * Note that the mobile app must be installed on the device for this to work.
 * Therefore, this function should be used in conjunction with a fallback URL for web users.
 *
 * The `navop` parameter is required and should be the name of the page to navigate to.
 *
 * @param params the query parameters to be added to the URL (required: `navop`, which is omitted from the query params URL)
 * @example `sagete://new-timesheet?user=123`
 * @returns the deep link URL
 */
export function buildMobileDeepLinkUrl(params) {
    const finalParams = new URLSearchParams(params.params);
    const destination = mapNavopsToScreen(params.navop, finalParams);
    const baseUrl = `${MOBILE_CUSTOM_SCHEME}://${destination}`;
    if (params.companyId) {
        finalParams.append('companyId', params.companyId);
    }
    if (params.loginId) {
        finalParams.append('loginId', params.loginId);
    }
    const qs = encodeURIComponent(finalParams.toString());
    if (qs) {
        return `${baseUrl}?${qs}`;
    }
    return baseUrl;
}
/**
 * This function maps a navop value to a screen name.
 * This is useful for deep linking to specific pages in the mobile app.
 *
 * @param navop the navop value to map to a screen name
 * @returns the screen name for the given navop value
 */
function mapNavopsToScreen(navop, pathParameters) {
    switch (navop) {
        // PA values are only available for backwards compatibility and should not be used in new code.
        case NAVOPS.PA.LISTS_TIMESHEET:
            // case NAVOPS.TT.LISTS_TIMESHEET:
            return 'timesheets';
        case NAVOPS.PA.EDIT_MY_TIMESHEET:
        case NAVOPS.TT.EDIT_MY_TIMESHEET:
        case NAVOPS.PA.LISTS_MY_TIMESHEET:
        case NAVOPS.TT.LISTS_MY_TIMESHEET: {
            const timesheetId = pathParameters?.get('timesheetId');
            if (pathParameters && timesheetId) {
                pathParameters.delete('timesheetId');
                return `timesheets/${timesheetId}`;
            }
            return 'timesheets';
        }
        case NAVOPS.TT.CREATE_MY_TIMESHEET:
        case NAVOPS.PA.CREATE_MY_TIMESHEET:
            return 'new-timesheet';
        case NAVOPS.TT.LISTS_TIMESHEET_SITTIMESOURCES:
        case NAVOPS.PA.LISTS_TIMESHEET_SITTIMESOURCES:
            return 'data-sources';
        case NAVOPS.TT.LISTS_TIMESHEET_SITPREFERENCES:
        case NAVOPS.PA.LISTS_TIMESHEET_SITPREFERENCES: {
            const path = pathParameters?.get('path');
            switch (path) {
                // Advanced isn't supported on mobile
                // case 'advanced':
                case 'notifications':
                    return 'settings/notifications';
                default:
                    return 'settings';
            }
        }
        default:
            return 'timesheets';
    }
}
/**
 * This function is the primary entry point for building a deep link URL.
 * It will contruct a URL that points to `https://time.sage.com/deep-linking?...` with the given query parameters.
 * The query parameters should include a `destination` parameter that specifies the page to navigate to.
 *
 * [Universal links](https://developer.apple.com/documentation/xcode/supporting-universal-links-in-your-app) are better than this approach,
 * but this is a simple way to handle deep linking without needing to update `intacct.com` configuration to handle universal links.
 *
 * The URL will redirect to the mobile app deep link URL if the mobile app is installed, else it will redirect to the Intacct web URL.
 *
 * @param options the options for building the deep link URL, `navop` is required in the query parameters
 * @returns the web URL that will redirect to the mobile app deep link URL if the mobile app is installed, else the Intacct web URL
 */
export function buildDeepLinkUrl(options) {
    const { webBaseUrl, params, loginId, companyId, navop, isembedded } = options;
    const qp = new URLSearchParams({
        ...(navop ? { navop: navop.toString() } : {}),
        companyId,
    });
    if (typeof isembedded === 'boolean')
        qp.append('isembedded', isembedded ? 'T' : 'F');
    if (loginId)
        qp.append('loginId', loginId);
    if (params) {
        params.forEach((value, key) => {
            qp.append(key, value);
        });
    }
    // Use # instead of ? to avoid logging the query parameters in the server logs
    return `${webBaseUrl}/deep-linking#${encodeURIComponent(qp.toString())}`;
}
//# sourceMappingURL=urls.js.map